import { TRACE_CONTEXT, TRACEPARENT } from '@/utils/trace'
import { toast } from 'react-toastify'
import {
  ApiAgentCreateRequest,
  ApiAgentCreateResponse,
  ApiAgentExecuteRequest,
  ApiAgentForkRequest,
  ApiAgentForkResponse,
  ApiAgentGenerateKeyArtResponse,
  ApiAgentGenerateLogoResponse,
  ApiAgentGetResponse,
  ApiAgentStartResponse,
  ApiAgentUpdateRequest,
  ApiAgentUpdateResponse,
} from 'sprites-types/api/agents'
import { Steps } from 'sprites-types/api/steps'
import { ApiUserWorkspaceResponse, ApiWorkspaceAgentsResponse } from 'sprites-types/api/users'
import { GraphicMessage, UpdateGraphicMessage } from 'sprites-types/api/v2/graphics'
import { reporter } from './reporter'

// TODO: setup API types here
export async function fetchUser() {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/users/me`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (response.status === 401 || response.status === 403) {
      // Auth-related errors - clear session and notify user
      toast.error('Your session has expired. Please log in again.')
      throw new Error('Authentication failed - session cleared')
    }

    if (response.status === 404) {
      // User not found - clear session and notify user
      toast.error('Your session has expired. Please log in again.')
      throw new Error('User not found - session cleared')
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch user: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching user:', error)
    throw error
  }
}

export async function fetchQuota() {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/users/me/quota`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch quota: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching quota:', error)
    throw error
  }
}

export async function fetchPaymentPortal(returnPath: string, productId: string = '') {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/payments/portal?returnPath=${returnPath}&productId=${productId}`,
      {
        credentials: 'include',
      }
    )

    if (response.status === 201) {
      const data = await response.json()
      return data.url as string
    } else if (!response.ok) {
      // Try to get error message from response
      let errorMessage = `Failed to fetch payment portal: ${response.status}`
      try {
        const errorData = await response.json()
        if (errorData.error) {
          errorMessage = errorData.error
        }
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage)
    } else {
      return null
    }
  } catch (error) {
    console.error('Error fetching payment portal:', error)
    reporter.captureMessage('Payment portal failure', 'error', {
      error: error instanceof Error ? error.message : String(error),
      productId,
      fingerprint: ['frontend-payment-portal-failure'],
    })
    throw error
  }
}

export async function fetchPaymentCheckout(
  itemId: string,
  successPath: string,
  cancelPath: string,
  referralId?: string | null,
  source?: 'onboarding' | 'credits' | 'pricing' | 'dashboard',
  planId?: string,
  planName?: string,
  billingInterval?: 'month' | 'year',
  priceId?: string
) {
  try {
    // Store payment details in sessionStorage for tracking on success
    if (typeof window !== 'undefined') {
      if (source) {
        sessionStorage.setItem('payment_source', source)
      }
      if (planId) {
        sessionStorage.setItem('payment_plan_id', planId)
      }
      if (planName) {
        sessionStorage.setItem('payment_plan_name', planName)
      }
      if (billingInterval) {
        sessionStorage.setItem('payment_billing_interval', billingInterval)
      }
      // Use priceId if provided, otherwise fall back to itemId (they're typically the same)
      const priceIdToStore = priceId || itemId
      if (priceIdToStore) {
        sessionStorage.setItem('payment_price_id', priceIdToStore)
      }
    }

    let url = `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/payments/checkout?itemId=${itemId}&successPath=${successPath}&cancelPath=${cancelPath}`

    if (referralId) {
      url += `&referral=${encodeURIComponent(referralId)}`
    }

    const response = await fetch(url, {
      credentials: 'include',
    })

    // Handle redirect
    if (response.status === 201) {
      const data = await response.json()
      return data
    } else if (!response.ok) {
      throw new Error(`Failed to fetch payment checkout: ${response.status}`)
    } else {
      return null
    }
  } catch (error) {
    console.error('Error fetching payment checkout:', error)
    reporter.captureMessage('Checkout failure', 'error', {
      error: error instanceof Error ? error.message : String(error),
      status:
        error instanceof Error && error.message.includes('Failed to fetch payment checkout')
          ? error.message.split(': ')[1]
          : undefined,
      itemId,
      planId: planId || 'unknown',
      source: source || 'unknown',
      fingerprint: ['frontend-checkout-failure'],
    })
    throw error
  }
}

export async function fetchPrice(productId: string) {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/payments/prices/${productId}`
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch price: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching price:', error)
    throw error
  }
}

export async function createAgent(workspaceId: string): Promise<ApiAgentCreateResponse> {
  const body: ApiAgentCreateRequest = {
    workspaceId,
  }
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const { error } = await response.json()
    if (response.status === 403) {
      toast.error(error)
    }
    throw new Error(`Failed to create agent: ${response.status}`)
  }

  return await response.json()
}

export async function forkAgent(
  workspaceId: string,
  agentId: string
): Promise<ApiAgentForkResponse> {
  const body: ApiAgentForkRequest = {
    workspaceId: workspaceId,
    agentId: agentId,
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/fork`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const { error } = await response.json()
    if (response.status === 403) {
      toast.error(error)
    }
    throw new Error(`Failed to fork agent: ${response.status}`)
  }

  return await response.json()
}

export async function fetchWorkspace(): Promise<ApiUserWorkspaceResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/users/me/workspace`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch workspace: ${response.status}`)
  }

  return await response.json()
}

export async function fetchAgent(
  agentId: string,
  requestInit?: RequestInit
): Promise<ApiAgentGetResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}`,
    {
      credentials: 'include',
      headers: {
        ...requestInit?.headers,
        'Content-Type': 'application/json',
      },
    } satisfies RequestInit
  )

  if (!response.ok) {
    console.log('failed to fetch agent', await response.json())
    throw new Error(`Failed to fetch agent: ${response.status}`)
  }

  return await response.json()
}

export async function fetchAgentSteps(agentId: string): Promise<Steps | null> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}/steps`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    console.log('failed to fetch', await response.json())
    console.error(`Failed to fetch agent steps: ${response.status}`)
    return null
  }

  return await response.json()
}

export async function fetchWorkspaceAgents(
  workspaceId: string
): Promise<ApiWorkspaceAgentsResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/workspaces/${workspaceId}/agents`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch workspace agents: ${response.status}`)
  }

  return await response.json()
}

export async function updateAgent(
  agentId: string,
  request: ApiAgentUpdateRequest
): Promise<ApiAgentUpdateResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to update agent: ${response.status}`)
  }

  return await response.json()
}

export async function startAgent(agentId: string): Promise<ApiAgentStartResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}/start`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to start agent: ${response.status}`)
  }

  return await response.json()
}

export async function deleteAgent(agentId: string): Promise<void> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to delete agent: ${response.status}`)
  }

  return await response.json()
}

export async function isAgentHealthy(appUrl: string): Promise<boolean> {
  try {
    const response = await fetch(`${appUrl}/health`)
    if (!response.ok) {
      return false
    }
    return true
  } catch {
    return false
  }
}

export async function reviveAndPollAgent(agentId: string): Promise<boolean> {
  const startAgentResponse = await startAgent(agentId)
  if (startAgentResponse.status === 'started') {
    return true
  }
  if (!startAgentResponse.appUrl) {
    console.error(`Agent ${agentId} is missing an app URL`)
    return false
  }

  let agentIsHealthy = false
  const maxAttempts = 30
  const delay = 2000
  for (let i = 0; i < maxAttempts; i++) {
    agentIsHealthy = await isAgentHealthy(startAgentResponse.appUrl)
    if (agentIsHealthy) {
      return true
    }
    await new Promise((resolve) => setTimeout(resolve, delay))
  }
  return false
}

export async function executeAgent(
  agentId: string,
  request: ApiAgentExecuteRequest,
  traceId: string,
  traceContext: string
): Promise<Response> {
  return await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}/execute`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // Force trace with X-Cloud-Trace-Context/traceparent
        [TRACEPARENT]: traceId,
        [TRACE_CONTEXT]: traceContext,
      },
      body: JSON.stringify(request),
    }
  )
}

export async function getExecuteLogs(traceId: string): Promise<Response> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/execute/logs`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        traceId,
      }),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to get logs: ${response.status}`)
  }

  console.log('Log request completed, returning response...')

  return response
}

export async function generateLogo(agentId: string): Promise<ApiAgentGenerateLogoResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}/generate-logo`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        agentId: agentId,
      }),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to generate logo: ${response.status}`)
  }

  return await response.json()
}

export async function generateKeyArt(
  agentId: string,
  imageIndex: number
): Promise<ApiAgentGenerateKeyArtResponse> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}/generate-key-art`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        agentId: agentId,
        imageIndex: imageIndex,
      }),
    }
  )
  if (!response.ok) {
    throw new Error(`Failed to generate key art: ${response.status}`)
  }

  return await response.json()
}

// TODO: This is temporary exist on the frontend. Will be moved to backend
export async function generateAgentMeta(
  conversation: { role: 'assistant' | 'user'; content: string }[]
) {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/llm-completion`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-5',
        messages: [
          {
            role: 'system',
            content: `
          Based on the conversation, generate app name and app description. They should be funny and awesome. You can use emojis to improve it.
          IMPORTANT - THE RESPONSE SHOULD BE JSON FORMAT.
          {
            name: '',
            description: ''
          }`,
          },
          ...conversation,
        ],
      }),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to generate agent name: ${response.status}`)
  }

  return await response.json()
}

/**
 * Fetch agent for server-side metadata generation (SSR/Next.js generateMetadata)
 */
export async function fetchAgentForMetadata(agentId: string): Promise<ApiAgentGetResponse | null> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/agents/${agentId}`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    if (response.ok) {
      return await response.json()
    }
    return null
  } catch (error) {
    console.error(`fetchAgentForMetadata: Failed fetching agentId ${agentId}`, error)
    return null
  }
}

/**
 * Fetch workflow metadata for server-side metadata generation (SSR/Next.js generateMetadata)
 */
export async function fetchWorkflowForMetadata(
  workflowId: string
): Promise<{ success: boolean; metadata?: any } | null> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/workflows/${workflowId}/metadata`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    if (response.ok) {
      return await response.json()
    }
    return null
  } catch (error) {
    console.error(`fetchWorkflowForMetadata: Failed fetching workflowId ${workflowId}`, error)
    return null
  }
}

// --- Graphics API functions ---

export async function uploadFile(
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    // 10 MB size limit
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      return { success: false, error: 'File is too large (max 10 MB)' }
    }

    // Accept only .html or .htm files
    if (!file.name.match(/\.(html?|HTML?)$/)) {
      return { success: false, error: 'Only .html or .htm files are allowed' }
    }

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/services/upload-file`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    )

    const data = await response.json()

    if (data.success && data.url) {
      return { success: true, url: data.url }
    } else {
      return { success: false, error: 'File upload failed' }
    }
  } catch (error) {
    console.error('File upload failed:', error)
    return { success: false, error: 'File upload failed' }
  }
}

export async function updateGraphicWithHtml(
  id: string,
  html: string
): Promise<{ success: boolean; graphic?: any; error?: string }> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/graphics/update-html`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: id, html: html }),
      }
    )

    if (!response.ok) {
      const { error } = await response.json()
      return { success: false, error: error || `Failed to update graphic, unknown error` }
    }

    const data = await response.json()
    return { success: true, graphic: data.graphic }
  } catch (error) {
    console.error('Error updating graphic:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export async function exportGraphic(
  id: string
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/graphics/${id}/export`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      const { error } = await response.json()
      throw new Error(error || `Failed to export graphic: ${response.status}`)
    }

    const data = await response.json()

    if (!data.success || !data.url) {
      throw new Error('Graphic export failed')
    }

    return { success: true, url: data.url }
  } catch (error) {
    console.error('Error exporting graphic:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export async function createGraphicFromPrompt(
  prompt: string
): Promise<{ success: boolean; graphic?: any; error?: string }> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/graphics/create-from-prompt`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      }
    )

    if (!response.ok) {
      const { error } = await response.json()
      throw new Error(error || `Failed to create graphic: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, graphic: data.graphic }
  } catch (error) {
    console.error('Error creating graphic from prompt:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export async function createGraphicFromFile(
  htmlFileUrl: string
): Promise<{ success: boolean; graphic?: any; error?: string }> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/graphics/create`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ htmlUrl: htmlFileUrl }),
      }
    )

    if (!response.ok) {
      const { error } = await response.json()
      throw new Error(error || `Failed to create graphic: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, graphic: data.graphic }
  } catch (error) {
    console.error('Error creating graphic from file:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export async function updateGraphic(
  graphicId: string,
  message: UpdateGraphicMessage,
  previousMessages?: GraphicMessage[]
): Promise<{ success: boolean; graphic?: any; error?: string; summary?: string }> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/graphics/${graphicId}/update`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message, previousMessages }),
      }
    )

    if (!response.ok) {
      const { error } = await response.json()
      throw new Error(error || `Failed to update graphic: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, graphic: data.graphic, summary: data.summary }
  } catch (error) {
    console.error('Error updating graphic:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

export async function fetchGraphic(
  id: string
): Promise<{ success: boolean; graphic?: any; error?: string }> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/graphics/${id}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      const { error } = await response.json()
      throw new Error(error || `Failed to fetch graphic: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, graphic: data.graphic }
  } catch (error) {
    console.error('Error fetching graphic:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

// --- Marketing API functions ---

export interface EmailCaptureRequest {
  email: string
  firstName?: string
  lastName?: string
  name?: string
}

export interface EmailCaptureResponse {
  success: boolean
  message?: string
  error?: string
}

export async function captureEmail(request: EmailCaptureRequest): Promise<EmailCaptureResponse> {
  try {
    // Use authenticated endpoint that works with existing user session (anonymous or authenticated)
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/users/me/capture-email`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    )

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `Failed to capture email: ${response.status}`,
      }
    }

    return {
      success: true,
      message: data.message || 'Email successfully captured',
    }
  } catch (error) {
    console.error('Error capturing email:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error occurred',
    }
  }
}

export async function fetchInstagramScraperResultStatus(resultId: string) {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/instagram-scraper/result-status?result_id=${encodeURIComponent(resultId)}`,
      {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch result status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('[Instagram Scraper] Error fetching result status:', error)
    throw error
  }
}
